import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, ArrowLeft } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { date: "", title: "", desc: "", tag: "" };

const inputCls =
  "w-full bg-[#141418] border border-[#2A2A34] rounded-lg px-4 py-3 text-sm text-[#F5F2EB] placeholder:text-stone-500 focus:border-[#F49D37] outline-none transition-colors";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Qualcosa è andato storto. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function formatDate(iso) {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function AdminPage() {
  const [auth, setAuth] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const loadEvents = () =>
    axios.get(`${API}/events`).then((r) => setEvents(r.data)).catch(() => {});

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(() => {
        setAuth(true);
        loadEvents();
      })
      .catch(() => setAuth(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setAuth(true);
      loadEvents();
      toast.success("Accesso effettuato");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setAuth(false);
  };

  const addEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/events`, form, { withCredentials: true });
      toast.success("Serata aggiunta al calendario");
      setForm(EMPTY);
      loadEvents();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const removeEvent = async (id) => {
    try {
      await axios.delete(`${API}/events/${id}`, { withCredentials: true });
      toast.success("Serata eliminata");
      loadEvents();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen bg-[#0D0D0F] text-[#F5F2EB] font-body">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="flex items-center justify-between mb-10">
          <Logo small />
          <a
            href="/"
            data-testid="admin-back-home-link"
            className="flex items-center gap-2 text-[11px] font-mono2 uppercase tracking-widest text-stone-400 hover:text-[#F49D37] transition-colors"
          >
            <ArrowLeft size={14} /> Torna al sito
          </a>
        </div>

        {auth === null && <p className="text-stone-400 text-sm">Caricamento...</p>}

        {auth === false && (
          <form
            onSubmit={login}
            data-testid="admin-login-form"
            className="bg-[#1C1C22] border border-[#2A2A34] rounded-2xl p-7 max-w-md"
          >
            <h1 className="font-display uppercase text-3xl tracking-wide">Area gestione</h1>
            <p className="text-sm text-stone-400 mt-2">Accedi per gestire le serate del calendario.</p>
            <div className="space-y-4 mt-6">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                data-testid="admin-email-input"
                className={inputCls}
              />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Password"
                data-testid="admin-password-input"
                className={inputCls}
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-[#D34E36] mt-4">
                {error}
              </p>
            )}
            <button
              type="submit"
              data-testid="admin-login-button"
              className="mt-6 w-full bg-[#D34E36] hover:bg-[#F49D37] hover:text-black text-[#F5F2EB] font-bold uppercase tracking-widest text-xs px-7 py-3.5 rounded-full transition-colors"
            >
              Accedi
            </button>
          </form>
        )}

        {auth === true && (
          <div data-testid="admin-panel">
            <div className="flex items-center justify-between mb-8">
              <h1 className="font-display uppercase text-3xl sm:text-4xl tracking-wide">
                Gestione <span className="text-[#D34E36]">serate</span>
              </h1>
              <button
                onClick={logout}
                data-testid="admin-logout-button"
                className="flex items-center gap-2 text-[11px] font-mono2 uppercase tracking-widest text-stone-400 hover:text-[#F49D37] transition-colors"
              >
                <LogOut size={14} /> Esci
              </button>
            </div>

            <form
              onSubmit={addEvent}
              data-testid="admin-event-form"
              className="bg-[#1C1C22] border border-[#2A2A34] rounded-2xl p-7 mb-10"
            >
              <h2 className="font-display uppercase text-2xl tracking-wide">Aggiungi una serata</h2>
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                <input
                  type="date"
                  required
                  value={form.date}
                  onChange={set("date")}
                  data-testid="event-date-input"
                  className={`${inputCls} [color-scheme:dark]`}
                />
                <input
                  required
                  value={form.title}
                  onChange={set("title")}
                  placeholder="Titolo (es. DJ Set, Halloween Party) *"
                  data-testid="event-title-input"
                  className={inputCls}
                />
                <input
                  value={form.tag}
                  onChange={set("tag")}
                  placeholder="Etichetta (es. Speciale) — opzionale"
                  data-testid="event-tag-input"
                  className={inputCls}
                />
                <input
                  value={form.desc}
                  onChange={set("desc")}
                  placeholder="Descrizione breve — opzionale"
                  data-testid="event-desc-input"
                  className={inputCls}
                />
              </div>
              <button
                type="submit"
                disabled={saving}
                data-testid="event-add-button"
                className="mt-5 flex items-center gap-2 bg-[#D34E36] hover:bg-[#F49D37] hover:text-black disabled:opacity-50 text-[#F5F2EB] font-bold uppercase tracking-widest text-xs px-7 py-3.5 rounded-full transition-colors"
              >
                <Plus size={14} /> {saving ? "Salvataggio..." : "Aggiungi serata"}
              </button>
            </form>

            <h2 className="font-display uppercase text-2xl tracking-wide mb-4">Serate in calendario</h2>
            {events.length === 0 && (
              <p data-testid="admin-events-empty" className="text-sm text-stone-500">
                Nessuna serata in programma. Aggiungine una qui sopra.
              </p>
            )}
            <ul className="space-y-3">
              {events.map((ev) => (
                <li
                  key={ev.id}
                  data-testid={`admin-event-row-${ev.id}`}
                  className="flex items-center justify-between gap-4 bg-[#1C1C22] border border-[#2A2A34] rounded-xl px-5 py-4"
                >
                  <div>
                    <p className="font-display uppercase text-lg tracking-wide">
                      {ev.title}
                      {ev.tag && (
                        <span className="ml-2 align-middle text-[9px] font-body font-bold tracking-[0.15em] uppercase bg-[#F49D37] text-black px-2 py-0.5 rounded-full">
                          {ev.tag}
                        </span>
                      )}
                    </p>
                    <p className="text-xs font-mono2 text-stone-400 mt-0.5">{formatDate(ev.date)}</p>
                    {ev.desc && <p className="text-sm text-stone-500 mt-1">{ev.desc}</p>}
                  </div>
                  <button
                    onClick={() => removeEvent(ev.id)}
                    data-testid={`event-delete-button-${ev.id}`}
                    aria-label={`Elimina ${ev.title}`}
                    className="shrink-0 text-stone-500 hover:text-[#D34E36] transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
